import axios from 'axios';
import aiImagesApi from 'common/src/modules/aiImages/aiImages.api';
import {CreativeAssetsSource} from 'common/src/modules/Assets/Assets.enums';
import {Asset} from 'common/src/modules/Assets/Assets.types';
import {BrandGuidelines} from 'common/src/modules/brandGuidelines/brandGuidelines.types';
import {OpenAIModel} from 'common/src/modules/shared/copilot/openAI/openAI.enums';
import getActiveComp from 'common/src/modules/creativeTemplates/copilot/agents/adBuilderAgent/utils/getActiveComp';
import {creativeTemplatesApi} from 'common/src/modules/creativeTemplates/creativeTemplates.api';
import addTemplateAssetsThunk from 'common/src/modules/creativeTemplates/store/creativeTemplatePage/movieAssets/actions/addTemplateAssets';
import {TemplateAssetType} from 'common/src/modules/dynamicParameters/dynamicParameters.enums';
import {TemplateAsset} from 'common/src/modules/dynamicParameters/dynamicParameters.types';
import openAiApi from 'common/src/modules/openAi/openAi.api';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import zodToJsonSchema from 'zod-to-json-schema';
import {AdBuilderAgentToolName} from '../adBuilderAgent.enums';

// Helper to convert base64 to File
function base64ToFile(base64: string, fileName: string, mimeType = 'image/png'): File {
  const arr = base64.split(',');
  const bstr = atob(arr[arr.length - 1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], fileName, {type: mimeType});
}

const referenceImageSchema = z.object({
  source: z.nativeEnum(CreativeAssetsSource),
  assetId: z.string(),
});

const inputSchema = z.object({
  imageCreationDetails: z
    .array(
      z.object({
        prompt: z
          .string()
          .describe(
            'Prompt for the image generation. you can @mention the reference image by its id',
          ),
        negativePrompt: z
          .string()
          .nullable()
          .optional()
          .describe('Negative prompt - what to avoid in the generated image'),
        purpose: z
          .string()
          .nullable()
          .optional()
          .describe('helps add the relevant brand guidelines based on image purpose'),
        enhancePrompt: z
          .boolean()
          .nullable()
          .optional()
          .default(true)
          .describe('Enhance the prompt with brand guidelines'),
        width: z.number().describe('Width of the image'),
        height: z.number().describe('Height of the image'),
        removeBackground: z
          .boolean()
          .nullable()
          .optional()
          .describe('Whether to remove the background from the generated image'),
        referenceImages: z
          .array(referenceImageSchema)
          .nullable()
          .optional()
          .describe(
            'Optional array of reference images with their source and asset id. If reference images mentioned in the prompt, you must provide this property',
          ),
      }),
    )
    .max(4)
    .describe('Array of image creation details (max 4)'),
});

type PromptToImageParams = z.infer<typeof inputSchema>;
type SingleImageCreationParams = PromptToImageParams['imageCreationDetails'][number];

type ImageCreationResult = {
  id: string;
  asset: TemplateAsset | null;
  error: string | null;
};

interface ImageGenerationResponse {
  id: string;
  error?: string;
}

// Schema for the enhanced prompt response
const enhancedPromptResponseSchema = z.object({
  ImageGuidelinesCategoryUsedAndWhy: z.string(),
  BrandColorUsedAndWhy: z.string(),
  enhancedPrompt: z.string(),
});

// Helper function to enhance prompt with brand guidelines
async function enhancePromptWithBrandGuidelines(
  originalPrompt: string,
  purpose: string | null | undefined,
  brandGuidelines: BrandGuidelines | null,
): Promise<string> {
  if (!brandGuidelines || !brandGuidelines.imageGuidelines?.length) {
    return originalPrompt;
  }

  try {
    const systemPrompt = [
      'You are an expert in enhancing image-generation prompts with brand guidelines (image needed as layer or element in ad).',
      'Keep the original intent and creativity,',
      'integrate relevant guidelines based on purpose,',
      'and return only the enhanced prompt.',
    ].join(' ');

    const userPrompt = `Original prompt: "${originalPrompt}"
${purpose ? `Image purpose: "${purpose}"` : 'Image purpose: Not available. Try to figure it out from the prompt.'}

Brand guidelines to consider:
${JSON.stringify(brandGuidelines.imageGuidelines, null, 2)}
${brandGuidelines.colors ? `Brand colors: ${JSON.stringify(brandGuidelines.colors, null, 2)}` : ''}

Please enhance the original prompt by incorporating relevant brand guidelines. Focus on:
- Visual style and composition
- Brand colors schemes
- Technical/design specs

In the response, please provide :
 - Image guidelines category used and why (just category name)
 - Brand colors used and why
 - The enhanced prompt.`;

    const completion = await openAiApi.createChatCompletion({
      model: OpenAIModel.Gpt4_1,
      temperature: 0.7,
      messages: [
        {
          role: 'system',
          content: systemPrompt,
        },
        {
          role: 'user',
          content: userPrompt,
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'enhancedPromptResponse',
          strict: true,
          schema: zodToJsonSchema(enhancedPromptResponseSchema),
        },
      },
    });

    const responseContent = completion.choices[0].message.content;

    if (!responseContent) {
      return originalPrompt;
    }

    const parsedResponse = enhancedPromptResponseSchema.parse(JSON.parse(responseContent));

    // Log the enhanced prompt and brand guidelines used

    if (parsedResponse.ImageGuidelinesCategoryUsedAndWhy) {
      console.log(
        'Image Guidelines category used and why:',
        parsedResponse.ImageGuidelinesCategoryUsedAndWhy,
      );
    }

    if (parsedResponse.BrandColorUsedAndWhy) {
      console.log('Image brand colors used and why:', parsedResponse.BrandColorUsedAndWhy);
    }

    console.log('Enhanced prompt:', parsedResponse.enhancedPrompt);

    return parsedResponse.enhancedPrompt;
  } catch (error) {
    console.error('Failed to enhance prompt with brand guidelines:', error);

    return originalPrompt;
  }
}

function promptToImageTool(store: CLStore) {
  return tool({
    name: AdBuilderAgentToolName.PromptToImage,
    displayName: 'Creating image/s from prompt',
    description:
      'Generates multiple images from prompts and saves them to the template assets library. Returns an array of asset IDs (empty string for failed generations).',
    inputSchema,
    execute: async ({input}: ToolExecuteParams<typeof inputSchema>) => {
      // Fetch brandGuidelines from Redux store
      const brandGuidelines = store.getState().brandGuidelinesPage.brandGuidelines.data;

      console.log('brandGuidelines', brandGuidelines);

      const activeComp = getActiveComp(store);

      if (!activeComp) {
        return {content: 'ERROR: No active composition found'};
      }

      const state = store.getState();
      const template = state.creativeTemplatePage.template;
      const templateId = template?.id;

      if (!templateId) {
        return {
          content: 'ERROR: No template ID found in store',
        };
      }

      try {
        // Process all image creation details in parallel
        const imagePromises = input.imageCreationDetails.map(
          async (imageParams: SingleImageCreationParams): Promise<ImageCreationResult> => {
            try {
              // Enhance prompt with brand guidelines if enabled
              const shouldEnhance = imageParams.enhancePrompt !== false; // default to true
              const finalPrompt = shouldEnhance
                ? await enhancePromptWithBrandGuidelines(
                    imageParams.prompt,
                    imageParams.purpose ?? undefined,
                    brandGuidelines,
                  )
                : imageParams.prompt;

              let images: string[];

              if (imageParams.referenceImages?.length) {
                // Get reference images from appropriate source
                const referenceImages: Array<{name: string; url: string}> = [];

                for (const refImage of imageParams.referenceImages) {
                  if (refImage.source === CreativeAssetsSource.TemplateAssets) {
                    // Get from template assets in store
                    const templateAssets =
                      store.getState().creativeTemplatePage.movieAssets.templateAssets;
                    const asset = templateAssets.find(
                      (a: TemplateAsset) => a.id === refImage.assetId,
                    );

                    if (!asset) {
                      throw new Error(
                        `Reference image with ID ${refImage.assetId} not found in template assets`,
                      );
                    }

                    referenceImages.push({
                      name: asset.metadata.name,
                      url: asset.metadata.url,
                    });
                  } else if (refImage.source === CreativeAssetsSource.BrandAssets) {
                    // Get from brand assets in Redux store
                    const state = store.getState();
                    const brandAssets = state.brandGuidelinesPage.brandAssets;

                    // Search in image assets only
                    const foundAsset = brandAssets.images?.find(
                      (asset: Asset) => asset.id === refImage.assetId,
                    );

                    if (!foundAsset) {
                      throw new Error(
                        `Reference image with ID ${refImage.assetId} not found in brand assets`,
                      );
                    }

                    referenceImages.push({
                      name: foundAsset.name,
                      url: foundAsset.url,
                    });
                  }
                }

                // Generate image with references using enhanced prompt
                const vendorService =
                  store.getState().copilot.settings.promptToImageWithReferenceImagesModel ?? 11;

                images = await aiImagesApi.promptToImageWithReferenceImages(
                  finalPrompt,
                  referenceImages,
                  imageParams.width,
                  imageParams.height,
                  1, // outputCount
                  vendorService,
                  {
                    negativePrompt: imageParams.negativePrompt ?? undefined,
                  },
                );
              } else {
                // Generate image without references using enhanced prompt
                const vendorService = store.getState().copilot.settings.promptToImageModel ?? 9;

                const response = await aiImagesApi.promptToImage({
                  prompt: finalPrompt,
                  negativePrompt: imageParams.negativePrompt ?? undefined,
                  width: imageParams.width,
                  height: imageParams.height,
                  outputCount: 1,
                  vendorService,
                });
                images = response.images;
              }

              if (!images?.length) {
                return {id: '', asset: null, error: 'No images generated'};
              }

              // Convert to File and save to template assets
              let file = base64ToFile(images[0], 'prompt-image.png');

              // Remove background if requested
              if (imageParams.removeBackground) {
                // Convert file to canvas
                const img = new Image();
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                await new Promise((resolve) => {
                  img.onload = () => {
                    canvas.width = img.width;
                    canvas.height = img.height;
                    ctx?.drawImage(img, 0, 0);
                    resolve(null);
                  };

                  img.src = URL.createObjectURL(file);
                });

                const imageWithoutBg = await aiImagesApi.removeBackground({
                  sourceCanvas: canvas,
                  vendorService: 3, // Stability AI for background removal
                  cancelToken: axios.CancelToken.source().token,
                });
                file = base64ToFile(imageWithoutBg, 'prompt-image-no-bg.png');
              }

              const asset = await creativeTemplatesApi.addAsset(
                templateId,
                file,
                TemplateAssetType.Image,
                undefined,
                {
                  ContentDescription: imageParams.prompt,
                  Source: 'Prompt To Image',
                },
              );

              return {id: asset.id, asset, error: null};
            } catch (error) {
              return {
                id: '',
                asset: null,
                error: (error instanceof Error ? error.message : String(error)).slice(0, 200),
              };
            }
          },
        );

        // Wait for all images to be processed
        const results = await Promise.all(imagePromises);

        // Update template assets using thunk
        const successfulAssets = results
          .filter((result) => result.asset && result.id !== '')
          .map((result) => result.asset) as TemplateAsset[];

        if (successfulAssets.length > 0) {
          store.dispatch(addTemplateAssetsThunk([...successfulAssets]));
        }

        return {
          content: JSON.stringify(
            results.map((result) => {
              const response: ImageGenerationResponse = {id: result.id};

              if (result.error) {
                response.error = result.error;
              }

              return response;
            }),
          ),
        };
      } catch (error) {
        return {
          content: `ERROR: ${error instanceof Error ? error.message : 'Failed to generate and save images'}`,
        };
      }
    },
  });
}

export default promptToImageTool;
